import { toDateKeyTz } from '@/lib/tz';
import { eventHasChild, type EventRow } from '@/lib/schedule';

/**
 * Ongoing multi-day events — the model behind the "All ongoing" section and
 * the pinned span bars (YAR-419).
 *
 * "Ongoing" is deliberately wider than "happening right now": it covers every
 * multi-day event that has not finished yet, active or upcoming, with no
 * distance cap. A tuition window opening in June is as ongoing as a book fair
 * ending tonight — both are things the parent cannot see from the week they
 * happen to be browsing, which is the whole reason these surfaces exist.
 */

export type OngoingKind = 'ending' | 'starting';
export type OngoingTint = 'error' | 'warning' | 'neutral';

export interface OngoingEvent {
  event: EventRow;
  /** Timezone-resolved YYYY-MM-DD of the first day the event covers. */
  startKey: string;
  /** Timezone-resolved YYYY-MM-DD of the last day the event covers. */
  endKey: string;
  /** Whether the next thing to happen is the event ending or starting. */
  kind: OngoingKind;
  /** The day something changes — end day when running, start day when not. */
  nextChangeKey: string;
  /** Whole days from today to `nextChangeKey`; 0 means today. */
  daysUntilChange: number;
  label: string;
  tint: OngoingTint;
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Whole days between two YYYY-MM-DD keys. Anchored at UTC noon so a DST
 * transition inside the span (a 23- or 25-hour day) can't round the
 * difference to the wrong integer — the failure mode that would turn
 * "Ends today" into "Ends tomorrow" for a single day each spring.
 */
export function dayDiff(fromKey: string, toKey: string): number {
  const [fy, fm, fd] = fromKey.split('-').map(Number);
  const [ty, tm, td] = toKey.split('-').map(Number);
  const from = Date.UTC(fy, fm - 1, fd, 12);
  const to = Date.UTC(ty, tm - 1, td, 12);
  return Math.round((to - from) / 86_400_000);
}

/** "Nov 27" — or "Jan 5, 2027" once the year stops being the current one. */
function formatAbsoluteDay(dateKey: string, todayKey: string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  const base = `${MONTHS[m - 1]} ${d}`;
  return String(y) === todayKey.slice(0, 4) ? base : `${base}, ${y}`;
}

/**
 * The state label and its tint, per YAR-419 ACs 4 and 5. The label text
 * always carries the meaning on its own — tint is never the only signal —
 * and no state is ever green: on this surface green reads as "fine", which
 * is exactly the wrong thing to say about a deadline.
 */
function describeState(
  kind: OngoingKind,
  days: number,
  changeKey: string,
  todayKey: string,
): { label: string; tint: OngoingTint } {
  if (kind === 'ending') {
    if (days <= 0) return { label: 'Ends today', tint: 'error' };
    if (days === 1) return { label: 'Ends tomorrow', tint: 'warning' };
    // N >= 2 only: 0 and 1 are spelled out above, so the singular
    // "1 day left" can never render.
    return {
      label: `${days} days left`,
      tint: days <= 3 ? 'warning' : 'neutral',
    };
  }

  if (days <= 0) return { label: 'Starts today', tint: 'neutral' };
  if (days === 1) return { label: 'Starts tomorrow', tint: 'neutral' };
  if (days <= 7) return { label: `Starts in ${days} days`, tint: 'neutral' };
  return {
    label: `Starts ${formatAbsoluteDay(changeKey, todayKey)}`,
    tint: 'neutral',
  };
}

export interface BuildOngoingOptions {
  /** Today in the parent's timezone; pass a live key so labels re-derive at midnight. */
  todayKey: string;
  userTimezone?: string | null;
  selectedChildId?: string | null;
}

/**
 * Select every unfinished multi-day event and rank it by next change date.
 *
 * Membership and order are GLOBAL — they do not depend on the week being
 * browsed (AC 16). Only the pinned bars re-scope to a week, and they do that
 * by filtering this list, never by re-sorting it.
 */
export function buildOngoingEvents(
  events: EventRow[],
  { todayKey, userTimezone, selectedChildId }: BuildOngoingOptions,
): OngoingEvent[] {
  const rows: OngoingEvent[] = [];

  for (const event of events) {
    if (!event.start_date || !event.end_date) continue;
    if (selectedChildId && !eventHasChild(event, selectedChildId)) continue;

    const startKey = toDateKeyTz(event.start_date, userTimezone);
    const endKey = toDateKeyTz(event.end_date, userTimezone);

    // Single-day events belong to the normal feed, not here.
    if (!startKey || !endKey || startKey === endKey) continue;
    // Finished: History owns it from its end date onward.
    if (endKey < todayKey) continue;

    // An event starting today has not started yet — its next change is the
    // start, so it reads "Starts today" and sorts behind anything ending
    // today (AC 3).
    const kind: OngoingKind = startKey < todayKey ? 'ending' : 'starting';
    const nextChangeKey = kind === 'ending' ? endKey : startKey;
    const daysUntilChange = dayDiff(todayKey, nextChangeKey);
    const { label, tint } = describeState(
      kind,
      daysUntilChange,
      nextChangeKey,
      todayKey,
    );

    rows.push({
      event,
      startKey,
      endKey,
      kind,
      nextChangeKey,
      daysUntilChange,
      label,
      tint,
    });
  }

  return rows.sort((a, b) => {
    if (a.nextChangeKey !== b.nextChangeKey) {
      return a.nextChangeKey < b.nextChangeKey ? -1 : 1;
    }
    // Same day: the one ending outranks the one starting.
    if (a.kind !== b.kind) return a.kind === 'ending' ? -1 : 1;
    if (a.startKey !== b.startKey) return a.startKey < b.startKey ? -1 : 1;
    return a.event.title.localeCompare(b.event.title);
  });
}

export interface SpanBarPlacement {
  ongoing: OngoingEvent;
  /** 0-based first column the bar occupies in the browsed week. */
  startCol: number;
  /** 0-based last column the bar occupies, inclusive. */
  endCol: number;
  /** The run began before this week — the left edge is squared off. */
  continuesLeft: boolean;
  /** The run carries on past this week — the right edge is squared off. */
  continuesRight: boolean;
}

/**
 * Lay the ongoing events out as bars across the seven columns of the browsed
 * week, dropping any that touch none of its days (AC 10).
 *
 * `weekKeys` is the week the user is LOOKING at; the tint each bar carries was
 * already resolved against TODAY when the event was ranked, and is passed
 * through untouched. That split is the point of AC 13: browsing forward to
 * March must not paint March's events as urgent just because they are the
 * nearest thing on screen.
 *
 * Order is inherited from `buildOngoingEvents`, never recomputed (AC 11).
 */
export function buildSpanBars(
  ongoing: OngoingEvent[],
  weekKeys: string[],
): SpanBarPlacement[] {
  if (weekKeys.length === 0) return [];
  const first = weekKeys[0];
  const last = weekKeys[weekKeys.length - 1];

  const placements: SpanBarPlacement[] = [];
  for (const row of ongoing) {
    // No overlap with the browsed week.
    if (row.endKey < first || row.startKey > last) continue;

    // Clamp to the week: a run that began earlier starts at column 0, one
    // that carries on ends at the last column. The bar always begins at the
    // event's own start day when that day is inside the week.
    const startCol = row.startKey <= first ? 0 : weekKeys.indexOf(row.startKey);
    const endCol =
      row.endKey >= last ? weekKeys.length - 1 : weekKeys.indexOf(row.endKey);

    // A key that is inside the range but absent from `weekKeys` would mean a
    // non-contiguous week; treat it as no overlap rather than guessing.
    if (startCol < 0 || endCol < 0 || endCol < startCol) continue;

    placements.push({
      ongoing: row,
      startCol,
      endCol,
      continuesLeft: row.startKey < first,
      continuesRight: row.endKey > last,
    });
  }
  return placements;
}

/**
 * Which of `dayKeys` a still-running span covers.
 *
 * ── Why the month grid needs this and the week strip does not (YAR-418) ────
 *
 * The week strip carries a run's middle with a bar under the cells
 * ({@link buildSpanBars}), so a day inside a run is communicated even though
 * its cell stays unfilled. The month grid has no bar, and under the
 * endpoints-only rule that leaves a run of any length showing **two lit days
 * and nothing else**.
 *
 * Measured against QA's own data: a tuition period running 2026-09-01 to
 * 2026-12-28 lights 2 days out of roughly 120, and October — wholly inside the
 * period — shows nothing at all, because neither endpoint falls in that month.
 * That is the reported bug, and it is not a rendering slip: it is what the rule
 * says to do.
 *
 * Filling every covered day the way an item-bearing day is filled would be the
 * wrong fix, and worse than the bug: a day with a real task to do would become
 * indistinguishable from a day merely inside a payment window. Hence a separate
 * channel — the cell says "act here", the band says "a period runs through
 * here" — which is the same two-channel split the week strip already makes.
 *
 * Deliberately NOT gated on whether the day is past. A run that is still going
 * covers the days it has already passed through, and the parent browsing to
 * October needs to see that as much as the parent looking at today. This does
 * not reopen YAR-385, which is about a COMPLETED item still showing a dot:
 * `buildOngoingEvents` has already dropped every run whose end date is behind
 * today, so the two sets are disjoint by construction.
 */
export function buildCoveredDays(
  ongoing: OngoingEvent[],
  dayKeys: readonly (string | null)[],
): Set<string> {
  const covered = new Set<string>();
  if (ongoing.length === 0) return covered;

  for (const key of dayKeys) {
    if (!key) continue;
    for (const row of ongoing) {
      if (row.startKey <= key && key <= row.endKey) {
        covered.add(key);
        break;
      }
    }
  }
  return covered;
}
